import Select, { MultiValue } from "react-select";
import { Add, School } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import { useEffect, useState } from "react";
import { FilterTurmas } from "../../components/filter/FilterTurmas";
import Colors from "../../components/constants/Colors";
import { headers } from "../../components/core/store/localStorage";
import axios from "axios";
import errorHandler from "../../components/core/store/errorHandler";
import {
  CustomDatagrid,
  CustomRowBase,
} from "../../components/datagrid/CustomDatagrid";

const currentUser = sessionStorage.getItem("userName");
const currentEmp = sessionStorage.getItem("empName");
const API_URL = sessionStorage.getItem("apiUrl");
const PAGE_SIZE = 30;

type Option = { value: string; label: string; ativo?: boolean };

export const Turmas = () => {
  const [searchValue, setSearchValue] = useState("");
  const [telaInicial, setTelaInicial] = useState(true);
  const [registerClass, setRegisterClass] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [codTurma, setCodTurma] = useState("");
  const [descricao, setDescricao] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // DataGrid é 0-based
  const [rowCount, setRowCount] = useState(0);
  const [titleLoading, setTitleLoading] = useState("");
  const [valModalOpen, setValModalOpen] = useState(false);
  const [valErrors, setValErrors] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [searchCodTurma, setSearchCodTurma] = useState("");
  const [searchDescricao, setSearchDescricao] = useState("");
  const [codDisciplinaList, setCodDisciplinaList] = useState([]);
  const [disciplinas, setDisciplinas] = useState<Option[]>([]);

  useEffect(() => {
    getTurmas();
    getDisciplinas();
    if (API_URL === null) window.location.reload();
  }, []);

  useEffect(() => {
    getTurmas();
  }, [page]);

  const searchTurmas = async () => {
    setTitleLoading("Buscando turmas...");
    setNotFound(false);
    setLoading(true);
    setTelaInicial(false);
    await getTurmas(); // agora de fato espera
  };

  const getTurmas = async () => {
    setLoading(true);
    setTitleLoading("Buscando turmas...");
    try {
      const apiPage = page + 1;

      let url = `${API_URL}/turma?page=${apiPage}`;

      if (searchCodTurma) url += `&filtro=${searchCodTurma}`;

      const response = await axios.get(url, headers());

      const { turmas, totalPages } = response.data;

      if (!turmas || turmas.length === 0) {
        setRows([]);
        setTelaInicial(false);
        setNotFound(true);
        return;
      }

      setTelaInicial(true);
      setRows(turmas);
      setRowCount((totalPages ?? 0) * PAGE_SIZE);
    } catch (err: any) {
      console.error("Erro ao buscar turmas:", {
        message: err.message,
        response: err.response?.data,
        stack: err.stack,
      });
      setTelaInicial(false);
      setNotFound(true);
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const getDisciplinas = () => {
    axios
      .get(`${API_URL}/disciplina/combo`, headers())
      .then((response) => {
        if (!response.data || !response.data)
          throw new Error("Estrutura de dados inválida");

        const resp = response.data.map((disciplinas: any) => ({
          value: disciplinas.id,
          label: disciplinas.descricao,
          ativo: !disciplinas.inativo,
        }));
        setCodDisciplinaList(resp);
      })
      .catch((err) => {
        console.error("Erro ao buscar códigos de disciplinas:", {
          message: err.message,
          response: err.response?.data,
          stack: err.stack,
        });
        errorHandler(err);
      });
  };

  const handleChangeDisciplinas = (vals: MultiValue<Option>) => {
    setDisciplinas(vals as Option[]);
  };

  const iniciarCadastro = () => {
    setCodTurma("");
    setNotFound(false);
    setDescricao("");
    setIsEditing(false);
    setTelaInicial(false);
    setRegisterClass(true);
    setNotFound(false);
  };

  const handleBack = async () => {
    setNotFound(false);
    setSearchCodTurma("");
    setSearchDescricao("");
    setCodTurma("");
    setDescricao("");
    setRegisterClass(false);
    setDisciplinas([]);
    try {
      await getTurmas(); // agora de fato espera
      if (rows.length === 0) {
        setTelaInicial(false);
        setNotFound(true);
      } else {
        setTelaInicial(true);
        setNotFound(false);
      }
    } finally {
      setLoading(false); // desliga sempre (sucesso/erro)
    }
  };

  const handleEdit = (r: CustomRowBase) => {
    // console.log(JSON.stringify(r));
    setIsEditing(true);
    setCodTurma(String(r.id ?? ""));
    setDescricao(r.descricao ?? "");
    setTelaInicial(false);
    setRegisterClass(true);

    // Mapear as disciplinas para o formato correto
    if (r.disciplinas && Array.isArray(r.disciplinas)) {
      const disciplinasMapeadas = r.disciplinas.map((disc) => ({
        value: disc.disciplinaId, // mapeia disciplinaId para value
        label: disc.descricao, // mapeia descricao para label
        ativo: true, // define como ativo (ou você pode ter essa informação no JSON)
      }));
      setDisciplinas(disciplinasMapeadas);
    } else {
      setDisciplinas([]);
    }
  };

  const cadastrarTurma = async () => {
    try {
      setIsWaiting(true);

      const errors: string[] = [];

      if (!codTurma || !codTurma.trim())
        errors.push("Informe o código da turma.");

      if (errors.length) {
        setValErrors(errors);
        setValModalOpen(true);
        setIsWaiting(false);
        return; // interrompe envio
      }

      // PAYLOAD PARA O BACK DA IVITECH
      const payload = {
        id: codTurma,
        inativo: false,
        descricao,
        disciplinas: disciplinas.map((d) => ({
          disciplinaId: d.value, // ou "id"
          descricao: d.label,
        })),
      };

      console.log(`${JSON.stringify(payload, null, 2)}`);
      await axios.post(`${API_URL}/turma`, payload, headers());
    } catch (err) {
      console.log(err);
    }

    // RESET DOS CAMPOS
    getTurmas();
    setCodTurma("");
    setDescricao("");
    setIsWaiting(false);
    setSearchCodTurma("");
    setSearchDescricao("");
    setTelaInicial(true);
    setRegisterClass(false);
    setDisciplinas([]);
  };

  return (
    <>
      <Grid wrap="wrap">
        <Grid
          size={{ xs: 6, sm: 7, md: 8, xl: 10 }}
          style={{
            marginTop: "-1.6rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 className={css(styles.pageTitle)} style={{ marginRight: "4px" }}>
            {registerClass ? "Cadastrar Turma" : "Turmas"}
          </h3>

          <div style={{ display: "flex", gap: "15px" }}>
            {registerClass ? (
              <button className={css(styles.topBtn)} onClick={handleBack}>
                Voltar
              </button>
            ) : (
              <button onClick={iniciarCadastro} className={css(styles.topBtn)}>
                <Tooltip
                  title="Adicionar nova turma"
                  sx={{ cursor: "pointer" }}
                >
                  <Add />
                </Tooltip>
              </button>
            )}
          </div>
        </Grid>
      </Grid>

      <hr
        style={{
          color: "#eee",
          marginTop: "-0.55rem",
          marginBottom: "1rem",
        }}
      />

      {!registerClass ? (
        <FilterTurmas
          searchCodTurma={searchCodTurma}
          setSearchCodTurma={setSearchCodTurma}
          searchTurmas={searchTurmas}
        />
      ) : null}

      {telaInicial ? (
        <Box>
          <Box sx={{ width: "100%", display: "grid", gap: 2, marginTop: 2 }}>
            <CustomDatagrid
              rows={rows}
              visibleColumns={["id", "descricao", "actions"]}
              extraColumns={[
                {
                  field: "id",
                  headerName: "Código",
                  minWidth: 100,
                  flex: 1,
                },
              ]}
              actions={["edit"]}
              onEdit={(r) => handleEdit(r)}
              // 🔽 paginação no servidor
              loading={loading}
              paginationMode="server"
              paginationModel={{ page, pageSize: PAGE_SIZE }}
              onPaginationModelChange={(model) => {
                if (model.page !== page) setPage(model.page);
                // ignora mudanças de pageSize (API fixa em 30)
              }}
              rowCount={rowCount}
            />
          </Box>
        </Box>
      ) : null}

      {registerClass ? (
        <Box sx={{ width: "100%", display: "grid", gap: 2, marginTop: 2 }}>
          <Card>
            <CardContent>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Typography
                    sx={{
                      backgroundColor: Colors.titleRowWithBackgroundColor,
                      pl: 1,
                      pt: 0.75,
                      mt: -1,
                    }}
                    variant="body2"
                    color={Colors.titleWithBackgroudColor}
                  >
                    <School fontSize={"inherit"} /> Informações da Turma
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 3.5, lg: 2 }}>
                  <TextField
                    fullWidth
                    required
                    placeholder="Código da Turma"
                    value={codTurma}
                    type="text"
                    onChange={(ev) => {
                      setCodTurma(ev.target.value);
                    }}
                    size={"small"}
                    color="primary"
                    label="Código da Turma"
                    disabled={isEditing}
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: (theme) => theme.palette.text.disabled,
                      },
                      "& .MuiOutlinedInput-root.Mui-disabled": {
                        backgroundColor: (theme) =>
                          theme.palette.action.disabledBackground,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: (theme) => theme.palette.action.disabled,
                        },
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: (theme) =>
                          theme.palette.text.disabled,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 8.5, lg: 5 }}>
                  <TextField
                    fullWidth
                    placeholder="Descrição"
                    value={descricao}
                    type="text"
                    onChange={(ev) => {
                      setDescricao(ev.target.value);
                    }}
                    size={"small"}
                    color="primary"
                    label="Descrição"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, lg: 5 }}>
                  <Select<Option, true>
                    name="disciplinas"
                    placeholder="Disciplinas da Turma"
                    isMulti
                    options={codDisciplinaList}
                    value={disciplinas}
                    onChange={handleChangeDisciplinas}
                    getOptionLabel={(opt) => `${opt.value} - ${opt.label}`}
                    getOptionValue={(opt) => String(opt.value)}
                    formatOptionLabel={(opt, { context }) =>
                      context === "menu" ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <span>{opt.value}</span>
                          <span>-</span>
                          <span>{opt.label}</span>
                        </div>
                      ) : (
                        `${opt.value} - ${opt.label}`
                      )
                    }
                    noOptionsMessage={() => "Sem resultados"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (b) => ({ ...b, zIndex: 2000 }),
                      menu: (b) => ({ ...b, zIndex: 2000 }),
                      // ...seus outros estilos
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid size={{ xs: 12 }} textAlign={"center"}>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                "&:disabled": {
                  color: Colors.muiPrimary,
                },
              }}
              // disabled={isWaiting}
              onClick={() => cadastrarTurma()}
            >
              {isWaiting ? <CircularProgress size={20} /> : "Confirmar"}
            </Button>
          </Grid>
        </Box>
      ) : null}

      {loading ? (
        <Box
          width={"100%"}
          height={"45vh"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid
            size={{ xs: 12, md: 12 }}
            textAlign={"center"}
            justifyContent={"center"}
            alignItems={"center"}
            bgcolor={"#eceff9ff"}
            borderRadius={5}
            padding={5}
            color={Colors.noResultFont}
          >
            <div
              style={{
                marginTop: "-0.5rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress color="primary" />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <p
                style={{
                  color: "#4b4b4b",
                  fontSize: 14,
                  marginBottom: "-1rem",
                }}
              >
                {titleLoading}
              </p>
            </div>
          </Grid>
        </Box>
      ) : null}

      {notFound ? (
        <Box
          width={"100%"}
          height={"45vh"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid
            size={{ xs: 12, md: 12 }}
            height={{ md: "20%", xs: "50%" }}
            display={"inline-flex"}
            textAlign={"center"}
            justifyContent={"center"}
            alignItems={"center"}
            bgcolor={"lightyellow"}
            borderRadius={5}
            padding={5}
            color={Colors.noResultFont}
          >
            <p
              style={{
                color: "#4b4b4b",
                fontSize: 14,
              }}
            >
              Sua pesquisa não retornou nenhum resultado.
            </p>
          </Grid>
        </Box>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    color: "#4b4b4b",
  },
  input: {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 400,
    color: "#808080",
    transitionDuration: "0.2s",
  },
  btn: {
    backgroundColor: "#669966",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.3rem 0.42rem 0.4rem",
    width: "100%",
    marginTop: "1.95rem",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#669966",
      backgroundColor: "transparent",
      borderColor: "#669966",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent",
      borderColor: "#669966",
      opacity: 0.6,
    },
  },
  btnDisabled: {
    backgroundColor: "#B3B3B3",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#B3B3B3",
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.3rem 0.42rem 0.4rem",
    width: "100%",
    marginTop: "1.95rem",
  },
  erro: {
    fontSize: 14,
    color: "red",
    textAlign: "center",
    fontWeight: 600,
    marginTop: "1rem",
    marginBottom: "-0.5rem",
  },
  topBtn: {
    cursor: "pointer",
    backgroundColor: "#004AAD",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#004AAD",
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.5rem 0.5rem 0.5rem",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#186ad6ff",
      backgroundColor: "transparent",
      borderColor: "#186ad6ff",
    },
    ":active": {
      color: "#004AAD",
      backgroundColor: "transparent",
      borderColor: "#004AAD",
      opacity: 0.6,
    },
  },
});
